import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useSupabase } from "@/auth/SupabaseProvider";

import { ApiError, apiFetch } from "./client";

/**
 * Manual-trigger mutation for the `/run` page.
 *
 * Wraps the orchestrator's `POST /api/jobs`. Kept in its own file so
 * the `/run` page pulls a focused surface rather than co-existing
 * with `jobs.ts` (which is read-only today). The SSE-ticket fetch is
 * driven directly inside `use-run-stream.ts` via `apiFetch`, since
 * each EventSource open needs a fresh ticket and a TanStack mutation
 * adds nothing over the bare call.
 */

/** Body for `POST /api/jobs` — opaque `context` mirrors the orchestrator. */
export interface DispatchJobBody {
  agentType: string;
  context: Record<string, unknown>;
}

export interface DispatchJobResponse {
  jobId: string;
  status: string;
}

/**
 * Start a new job. The 409 `tools_draft_non_empty` response surfaces as
 * an `ApiError` whose `body.error === "tools_draft_non_empty"`; the
 * caller renders an inline banner with a link to the review queue.
 */
export function useDispatchJob() {
  const { client } = useSupabase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DispatchJobBody) =>
      apiFetch<DispatchJobResponse>(client, "/api/jobs", {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      // The new job affects the list and the (rare) edge where another
      // session was watching the queue badge. Invalidate broadly — the
      // detail page populates from its own query.
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

/** True iff the error is the orchestrator's 409 `tools_draft_non_empty`. */
export function isToolsDraftNonEmptyError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    typeof err.body === "object" &&
    err.body !== null &&
    (err.body as { error?: unknown }).error === "tools_draft_non_empty"
  );
}

/**
 * True iff the error is the orchestrator's 409 `tools_draft_empty`. Fired
 * when the validator is dispatched (manual-trigger path) against a
 * `tools_draft` with no unrated rows — the run would otherwise land in a
 * stuck `pending_review` because there's nothing to score.
 */
export function isToolsDraftEmptyError(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    typeof err.body === "object" &&
    err.body !== null &&
    (err.body as { error?: unknown }).error === "tools_draft_empty"
  );
}
